// src/components/HomePageSessions.tsx
import React, { useMemo } from 'react';
import SectionCard from './SectionCard';
import {
  HistoryOutlined,
  LaptopOutlined,
  MobileOutlined,
  GlobalOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Empty,
  Flex,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import { useLanguage } from 'src/providers/LanguageProvider';
import { ISession } from 'src/hooks/useSessions';
import { useNavigate } from 'react-router';
import useFormatDate from 'src/hooks/useFormatDate';
import useRelativeTimeFromNow from 'src/hooks/useRelativeTimeFromNow';

const { Text } = Typography;

type Props = {
  sessions: ISession[];
  loading?: boolean;
  error?: string | null;
  maxVisible?: number;          // default: 5
  onViewAll?: () => void;
  onRefresh?: () => void;

  /**
   * Opsiyonel: mevcut istemcinin ClientId değeri.
   * Eğer sağlarsanız, eşleşen oturum "Bu cihaz" etiketi alır.
   */
  currentClientId?: string;
};

function deviceIcon(s: ISession) {
  const hint = `${s.Platform || ''} ${s.UserAgent || ''}`.toLowerCase();
  const isMobile = /iphone|ipad|ipod|android|mobile/.test(hint);
  return isMobile ? <MobileOutlined /> : <LaptopOutlined />;
}

function safeDate(d?: string | null) {
  const dt = d ? new Date(d) : null;
  return dt && !Number.isNaN(dt.getTime()) ? dt : null;
}




const HomePageSessions: React.FC<Props> = ({
  sessions,
  loading = false,
  error = null,
  maxVisible = 5,
  onViewAll,
  onRefresh,
  currentClientId,
}) => {
  const { t } = useLanguage();
  const { token } = theme.useToken();

  const navigate = useNavigate();

  const relativeTimeFromNow = useRelativeTimeFromNow();

  // "en güncel" ölçütü: LastActivityAt > UpdatedAt > CreatedAt
  const getLastSeen = (s: ISession) =>
    s.LastActivityAt || s.UpdatedAt || s.CreatedAt || null;

  const sorted = useMemo(() => {
    const by = [...(sessions ?? [])];
    by.sort((a, b) => {
      const aT = safeDate(getLastSeen(a))?.getTime() ?? 0;
      const bT = safeDate(getLastSeen(b))?.getTime() ?? 0;
      return bT - aT;
    });
    return by.slice(0, maxVisible);
  }, [sessions, maxVisible]);

  const formatDate = useFormatDate();

  return (
    <SectionCard
      icon={<HistoryOutlined />}
      title={t('SESSIONS')}
      extra={
        <Space>
          {onRefresh && (
            <Button size="small" onClick={onRefresh}>
              {t('REFRESH')}
            </Button>
          )}
          <Button type="text" size="small" onClick={() => navigate('/security')}>
            {t('VIEW_ALL')}
          </Button>

        </Space>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <Text type="secondary">
          {t(
            'RECENT_SESSIONS_DESC')}
        </Text>

        {error ? (
          <Alert type="error" showIcon message={t('ERROR', 'Hata')} description={error} />
        ) : loading ? (
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <Skeleton active avatar paragraph={{ rows: 1 }} />
            <Skeleton active avatar paragraph={{ rows: 1 }} />
            <Skeleton active avatar paragraph={{ rows: 1 }} />
            <Skeleton active avatar paragraph={{ rows: 1 }} />
            <Skeleton active avatar paragraph={{ rows: 1 }} />
          </Space>
        ) : sorted.length === 0 ? (
          <Empty
            description={t('NO_SESSIONS', 'Kayıtlı oturum bulunamadı')}
            imageStyle={{ height: 60 }}
            style={{ marginBlock: 8 }}
          />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={10}>
            {sorted.map((s, idx) => {
              // "Bu cihaz" etiketi: currentClientId sağlanırsa ve eşleşirse gösterilir
              const isCurrent = !!currentClientId && s.ClientId === currentClientId;
              const dotColor = isCurrent ? 'success' : 'default';
              const location = [s.City, s.Country].filter(Boolean).join(', ');
              const last = getLastSeen(s);

              // UserAgent'tan tarayıcı kısmını kaba bir ayrıştırma ile almaya çalış
              const uaTail =
                s.UserAgent?.includes(') ')
                  ? s.UserAgent.split(') ').pop()
                  : s.UserAgent || '';

              return (
                <Flex
                  key={(s.Id as React.Key) ?? idx}
                  align="center"
                  justify="space-between"
                  gap={12}
                  style={{
                    width: '100%',
                    padding: 10,
                    borderRadius: 10,
                    border: `1px solid ${token.colorSplit}`,
                    background: token.colorBgContainer,
                  }}
                >
                  {/* Sol taraf: avatar + metinler */}
                  <Flex align="center" gap={12} style={{ minWidth: 0 }}>
                    <Badge dot color={dotColor} offset={[-2, 30]}>
                      <Avatar shape="square" size={40} icon={deviceIcon(s)} />
                    </Badge>

                    <div style={{ minWidth: 0 }}>
                      <Flex align="center" gap={8} wrap>
                        <Text strong ellipsis style={{ display: 'block', maxWidth: 300 }}>
                          {s.Device || s.Platform || t('UNKNOWN_DEVICE', 'Bilinmeyen cihaz')}
                        </Text>
                        {isCurrent && (
                          <Tag color="processing" bordered={false}>
                            {t('THIS_DEVICE', 'Bu cihaz')}
                          </Tag>
                        )}
                      </Flex>

                      <Space size={10} wrap>
                        {s.IpAddress && (
                          <Tag bordered={false} icon={<GlobalOutlined />}>
                            {s.IpAddress}
                          </Tag>
                        )}
                        {location && <Text type="secondary">{location}</Text>}
                        {uaTail && <Text type="secondary">{uaTail}</Text>}
                        {/* İsterseniz aşağıdaki ek bilgileri de küçük etiketler olarak gösterebilirsiniz */}
                        {s.Connection && <Tag bordered={false}>{s.Connection}</Tag>}
                        {s.Screen && <Tag bordered={false}>{s.Screen}</Tag>}
                        {s.Language && <Tag bordered={false}>{s.Language}</Tag>}
                        {s.Timezone && <Tag bordered={false}>{s.Timezone}</Tag>}
                      </Space>
                    </div>
                  </Flex>

                  {/* Sağ taraf: zaman */}
                  <Space direction="vertical" size={0} style={{ textAlign: 'right' }}>
                    <Tooltip title={t('TOOLTIP_LAST_ACTIVITY_AT') + formatDate(last)}   >
                      <Space size={6}>
                        <ClockCircleOutlined />
                        <Text type="secondary">{relativeTimeFromNow(last)}</Text>
                      </Space>
                    </Tooltip>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('FIRST_SEEN')}: {formatDate(s.CreatedAt)}
                    </Text>
                  </Space>
                </Flex>
              );
            })}
          </Space>
        )}
      </Space>
    </SectionCard>
  );
};

export default HomePageSessions;
